import { Key, useCallback, useMemo } from 'react'
import clsx from 'clsx'
import { Form, Tag, Text, Tree, TreeProps } from 'components/common'
import { price } from 'lib/str-formatter'
import { colors } from 'lib/colors'
import {
  OrderLineItem,
  ProductAvailabilitiesQuery,
} from 'lib/graphql/storeadmin'

export interface ForSaleVariantTree {
  key: string
  title: JSX.Element
  price: number
  forSale: boolean
  disableCheckbox: boolean
}

export interface ForSaleTree extends Omit<TreeProps, 'children'> {
  key: string
  title: JSX.Element
  children: ForSaleVariantTree[]
}

export const saleTag = (sale: boolean, isSelected = false): JSX.Element => {
  if (sale) return <Tag color={colors.NAE}>〇予約可</Tag>
  else if (isSelected) return <Tag color={colors.RURI}>選択中</Tag>
  else return <Tag color={colors.BENIHI}>×予約不可</Tag>
}

export interface VariantSelectTreeProps {
  data?: ProductAvailabilitiesQuery
  checkedKeys: Key[]
  formName: string
  oldBooks?: Key[]
  onCheck: (keys: Key[]) => void
  lineItems: OrderLineItem[]
}

export const VariantSelectTree = ({
  data,
  checkedKeys,
  formName,
  oldBooks,
  onCheck,
  lineItems,
}: VariantSelectTreeProps): JSX.Element => {
  // 選択中のバリエーションに対応するキーが存在するかどうか
  const isKeyExist = useCallback(
    (key: string): boolean => {
      return lineItems?.some(({ variant }) => variant === key)
    },
    [lineItems]
  )

  const dataSource = useMemo((): Array<ForSaleTree> => {
    const returningData = []
    data?.productAvailabilities?.edges?.forEach(({ product }) => {
      const variants = product?.variants?.edges
      if (variants?.length === 1) {
        returningData.push({
          key: variants?.[0]?.variant?.variantId,
          title: (
            <div className={clsx('flex', 'items-center', 'justify-between')}>
              <div>{product?.productName.jaJp}</div>
              <div style={{ paddingLeft: '5px' }}>
                {price(variants?.[0]?.variant?.price)}
              </div>
              <div style={{ paddingLeft: '5px' }}>
                {saleTag(
                  variants?.[0]?.variant?.forSale,
                  isKeyExist(variants?.[0]?.variant?.variantId)
                )}
              </div>
            </div>
          ),
          // 予約不可ならチェックボックスを無効にする
          disableCheckbox: isKeyExist(variants?.[0]?.variant?.variantId)
            ? false
            : !variants[0]?.variant?.forSale,
        })
      } else {
        const children = []
        const dataWithChildren = {
          key: product?.productId,
          title: <Text>{product?.productName.jaJp}</Text>,
          // 子要素が全て予約不可なら親要素のチェックボックスを無効にする
          disableCheckbox: isKeyExist(variants?.[0]?.variant?.variantId)
            ? false
            : variants?.every(({ variant }) => variant?.forSale === false),
        }
        variants?.forEach(({ variant }) => {
          let forSale = variant?.forSale
          if (oldBooks?.some((book) => variant?.variantId === book)) {
            forSale = true
          }
          children.push({
            key: variant?.variantId,
            title: (
              <p className={clsx('flex', 'items-center', 'justify-between')}>
                <span>{variant?.title.jaJp}</span>
                <span className="mx-2">{price(variant?.price)}</span>
                {saleTag(forSale, isKeyExist(variant?.variantId))}
              </p>
            ),
            price: variant?.price,
            forSale: forSale,
            disableCheckbox: isKeyExist(variant?.variantId) ? false : !forSale,
          })
        })
        dataWithChildren['children'] = children
        returningData.push(dataWithChildren)
      }
    })
    return returningData
  }, [data, isKeyExist, oldBooks])

  return (
    <Form.Item
      name={formName}
      valuePropName="checkedKeys"
      initialValue={checkedKeys}
    >
      <Tree checkable onCheck={onCheck} treeData={dataSource} />
    </Form.Item>
  )
}
